import React, { Component } from 'react';
import PropTypes from 'prop-types';

import {
  responsiveFlavors as responsiveFlavorsPropType,
  computed as ComputedPropType,
} from 'lib/CustomPropTypes';

import BTE from 'lib/BTE';
import BrowserDetection from 'lib/BrowserDetection';
import { imageAltForItem } from 'lib/imageAlt';
import { getTeaseUrl } from 'lib/teaseUtils';

// TODO: [BENTO-9041] combine dynamic image resize utils/libs (AIMS)
import { getAimsUrlFlavor } from 'lib/getAimsUrl';
import { isAimsSourceImage, getAimsSourceImage } from 'lib/isAimsUrl';

import AIMS_FLAVORS, { AIMS_FLAVOR_VALUES } from 'lib/aimsFlavors';
import {
  IMAGE_SIZES,
  getTelemundoImageSrc,
  getWidthAndHeightFromAIMSFlavor,
} from 'lib/imageUtils';

import Placeholder from './Placeholder';

const SIZE_MAP = {
  s: 320,
  m: 758,
  l: 1000,
  xl: 1240,
};

class Picture extends Component {
  static propTypes = {
    url: PropTypes.string,
    alt: PropTypes.string,
    className: PropTypes.string,
    layout: PropTypes.string,
    flavor: PropTypes.string,
    responsiveFlavors: responsiveFlavorsPropType,
    placeholder: PropTypes.bool,
    placeholderColor: PropTypes.string,
    aimsOptions: PropTypes.shape({
      resample: PropTypes.number,
      compression: PropTypes.number,
      type: PropTypes.number,
    }),
    originalHeight: PropTypes.number,
    originalWidth: PropTypes.number,
    lazyloaded: PropTypes.bool,
    computedValues: ComputedPropType,
    retina: PropTypes.bool,
  };

  static contextTypes = {
    amp: PropTypes.bool,
  };

  static defaultProps = {
    url: null,
    alt: '',
    className: '',
    layout: 'responsive',
    flavor: AIMS_FLAVORS.FOCAL,
    responsiveFlavors: {
      s: AIMS_FLAVORS.FOCAL,
      m: AIMS_FLAVORS.FOCAL,
      l: AIMS_FLAVORS.FOCAL,
      xl: AIMS_FLAVORS.FOCAL,
    },
    placeholder: false,
    placeholderColor: 'knockout-secondary',
    aimsOptions: {},
    originalHeight: null,
    originalWidth: null,
    lazyloaded: false,
    computedValues: null,
    retina: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      error: false,
    };
  }

  componentDidMount() {
    const { lazyloaded } = this.props;

    if (lazyloaded) {
      // Re-trigger picturefill srcset polyfill for IE 11
      if (window && window.picturefill && BrowserDetection.isIE()) {
        window.picturefill();
      }
    }
  }

  getURL = (size) => {
    const { computedValues, url } = this.props;

    const useTelemundoUrl = typeof url === 'string' && url.includes('telemundo.com');

    if (computedValues) {
      const { responsiveFlavors } = this.props;
      const flavor = responsiveFlavors ? responsiveFlavors[size] : '';
      const { width, height } = getWidthAndHeightFromAIMSFlavor(flavor);
      const isSquare = width && height && (width === height);
      return getTeaseUrl({ computedValues }, isSquare, useTelemundoUrl);
    }

    if (useTelemundoUrl) {
      return url;
    }

    return getAimsSourceImage(url);
  }

  getAlt = () => {
    const { alt, computedValues } = this.props;

    if (computedValues) {
      return imageAltForItem({ computedValues });
    }
    return alt;
  }

  srcSet = (size) => {
    const { retina } = this.props;
    let src = this.src(size);
    if (src) {
      if (retina) {
        // Make sure both 1x and 2x AIMS flavors exist
        src = `${this.src(size, 2)} 2x, ${src} 1x`;
      }
      if (size === 's') {
        return <source srcSet={src} />;
      }
      return (
        <source
          media={`(min-width: ${SIZE_MAP[size]}px)`}
          srcSet={src}
        />
      );
    }
    return null;
  }

  src = (size, scaleFactor = false) => {
    const { responsiveFlavors } = this.props;

    if (responsiveFlavors[size]) {
      let { flavor } = this.props;
      if (!AIMS_FLAVOR_VALUES.includes(flavor)) {
        this.warnFlavorDoesNotExist(flavor);
      }

      const url = this.getURL(size);
      if (url) {
        flavor = responsiveFlavors[size];

        const dimensions = getWidthAndHeightFromAIMSFlavor(flavor);
        if (scaleFactor && dimensions.width && dimensions.height && dimensions.replace) {
          const height = dimensions.height * scaleFactor;
          const width = dimensions.width * scaleFactor;
          const retinaFlavor = dimensions.replace(width, height);

          // Check if the retina flavor exists
          if (AIMS_FLAVOR_VALUES.includes(retinaFlavor)) {
            flavor = retinaFlavor;
          } else {
            this.warnFlavorDoesNotExist(retinaFlavor, true);
          }
        }

        if (url.includes('telemundo.com')) {
          return getTelemundoImageSrc(url, flavor);
        }
        return getAimsUrlFlavor(url, { flavor });
      }
    }
    return null;
  }


  /**
   * @param {string} flavor
   * @param {boolean} isRetina
   */
  warnFlavorDoesNotExist = (flavor, isRetina = false) => {
    const message = isRetina
      ? `Retina flavor ${flavor} doesn't exist, falling back to non-retina image`
      : `Flavor ${flavor} doesn't exist`;
    // eslint-disable-next-line no-undef
    if (__CLIENT__) {
      // eslint-disable-next-line no-console
      console.warn(message);
    }
  }

  imageLoaded = () => {
    BTE.oneImageLoaded();
  }

  imageError = () => {
    this.setState({ error: true });
  }

  picture = () => {
    const { error } = this.state;

    const {
      className,
      lazyloaded,
      retina,
    } = this.props;

    const hidden = error ? { visibility: 'hidden' } : {};

    return (
      <picture
        className={className}
        style={hidden}
        data-lazyloaded={lazyloaded ? 'true' : undefined}
      >
        {this.srcSet('xl')}
        {this.srcSet('l')}
        {this.srcSet('m')}
        {!!retina && this.srcSet('s')}
        <img
          src={this.src('s')}
          alt={this.getAlt()}
          onLoad={this.imageLoaded}
          onError={this.imageError}
        />
      </picture>
    );
  }

  ampImg = () => {
    const {
      responsiveFlavors, layout, originalWidth, originalHeight, url,
    } = this.props;

    // Use large, medium, or small image as default depending on availability
    let defaultImageSize = null;
    [...IMAGE_SIZES]
      .reverse()
      .forEach((sizeKey, i) => {
        if (i && responsiveFlavors[sizeKey] && !defaultImageSize) {
          defaultImageSize = sizeKey;
        }
      });

    // amp-img requires height - calculate for responsive images.
    Object.entries(responsiveFlavors).forEach((item) => {
      const [size, dimensions] = item;
      responsiveFlavors[size] = dimensions;
    });

    // Build srcset attribute.
    const ampSrcSet = () => Object.keys(responsiveFlavors)
      .map((size) => {
        const src = this.src(size, false);
        if (src) {
          return `${src.replace(/\s/g, '%20')} ${SIZE_MAP[size]}w`;
        }
        return null;
      })
      // Remove nulls
      .filter((size) => size)
      // Join into comma separated string
      .join(',');

    // Width and height attributes from small size.
    const { width, height } = getWidthAndHeightFromAIMSFlavor(responsiveFlavors[defaultImageSize]);

    const ampHeight = originalWidth && originalHeight
      ? (Math.floor(width * (originalHeight / originalWidth)))
      : height;

    return (
      <amp-img
        alt={this.getAlt()}
        src={this.src(defaultImageSize, false)}
        width={width}
        height={ampHeight}
        layout={layout || 'responsive'}
        srcset={isAimsSourceImage(url) ? ampSrcSet() : null}
        // Prevent AMP from calculating sizes attribute
        sizes=""
      />
    );
  }

  render() {
    const { amp } = this.context;

    if (amp) {
      return this.ampImg();
    }

    const { error } = this.state;

    const {
      url, className, placeholder, responsiveFlavors, computedValues, placeholderColor,
    } = this.props;

    const hasURL = url || getTeaseUrl({ computedValues });
    if (placeholder && (error || !hasURL)) {
      return (
        <Placeholder
          className={className}
          responsiveFlavors={responsiveFlavors}
          color={placeholderColor}
        />
      );
    }

    return this.picture();
  }
}

export default Picture;
